import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import ContentfulContext from './ContentfulContext'
import Box from '@mui/material/Box'
import { createClient } from 'contentful'
import { isEmpty } from 'lodash'
import { SURVEY_ID } from '../data/surveyID'

export const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
})

const CONTENTFUL_IDS = {
  [`/`]: `landingPage`,
  [`/imagine/${SURVEY_ID.SLOW_NO_GROWTH}`]: 'chooseATopicToExplore',
  [`/imagine/${SURVEY_ID.GROWING_RURAL}`]: 'chooseATopicToExplore',
  [`/imagine/${SURVEY_ID.URBAN}`]: 'chooseATopicToExplore',
  [`/imagine/${SURVEY_ID.SLOW_NO_GROWTH}/1`]: 'chooseATopicToExplore',
  [`/imagine/${SURVEY_ID.GROWING_RURAL}/1`]: 'chooseATopicToExplore',
  [`/imagine/${SURVEY_ID.URBAN}/1`]: 'chooseATopicToExplore',
  [`/topic/${SURVEY_ID.SLOW_NO_GROWTH}/water`]: '3mMcsL9F5lwsykvudzJEaI',
  [`/topic/${SURVEY_ID.SLOW_NO_GROWTH}/housing`]: '3ME3fgQXwsmRR8drahoAPg',
  [`/topic/${SURVEY_ID.SLOW_NO_GROWTH}/open-space-and-recreation`]: '23UrV6rlZDYDaoJqujSg7N',
  [`/topic/${SURVEY_ID.SLOW_NO_GROWTH}/transportation`]: '72Jn4TF2mGQg5klM9Y1OEq',
  [`/topic/${SURVEY_ID.GROWING_RURAL}/water`]: 'qI9YOOMlsBIN9v1vRPCRi',
  [`/topic/${SURVEY_ID.GROWING_RURAL}/housing`]: 'GZANb2pTCM0gjgmzPiYWs',
  [`/topic/${SURVEY_ID.GROWING_RURAL}/open-space-and-recreation`]: '2YC6hNod0rNIPzOX0nDehL',
  [`/topic/${SURVEY_ID.GROWING_RURAL}/transportation`]: '2fOGfdxeVfD91WgR1LdHcQ',
  [`/topic/${SURVEY_ID.URBAN}/water`]: '78vDj3uda1ERYySJLpDjfX',
  [`/topic/${SURVEY_ID.URBAN}/housing`]: '2Rhv5GeYxJoJ1sij1wgO2',
  [`/topic/${SURVEY_ID.URBAN}/open-space-and-recreation`]: '4QIUi7m8tGXsO5TTPsd3Rl',
  [`/topic/${SURVEY_ID.URBAN}/transportation`]: '4r5Js4WTJRj6fO2ZKpYP9',
  [`/big-ideas/${SURVEY_ID.SLOW_NO_GROWTH}/water`]: '2DwQKudWtFRk25GdIbFao8',
  [`/big-ideas/${SURVEY_ID.SLOW_NO_GROWTH}/housing`]: '1ArtryJTwec1AL2hArakpU',
  [`/big-ideas/${SURVEY_ID.SLOW_NO_GROWTH}/open-space-and-recreation`]: '6GYrB1d0GicAx9sloAjHUg',
  [`/big-ideas/${SURVEY_ID.SLOW_NO_GROWTH}/transportation`]: '4pvUKjUlFh4ubU1jptm9yd',
  [`/big-ideas/${SURVEY_ID.GROWING_RURAL}/water`]: '2DwQKudWtFRk25GdIbFao8',
  [`/big-ideas/${SURVEY_ID.GROWING_RURAL}/housing`]: '1ArtryJTwec1AL2hArakpU',
  [`/big-ideas/${SURVEY_ID.GROWING_RURAL}/open-space-and-recreation`]: '6GYrB1d0GicAx9sloAjHUg',
  [`/big-ideas/${SURVEY_ID.GROWING_RURAL}/transportation`]: '4pvUKjUlFh4ubU1jptm9yd',
  [`/big-ideas/${SURVEY_ID.URBAN}/water`]: '5qCiQxP6DXrX8r944092qF',
  [`/big-ideas/${SURVEY_ID.URBAN}/housing`]: 'NzxpDu1ULPCRST4HHDF2d',
  [`/big-ideas/${SURVEY_ID.URBAN}/open-space-and-recreation`]: '2h0LJtEDclugIDbWXLCBfh',
  [`/big-ideas/${SURVEY_ID.URBAN}/transportation`]: '0dQ6hmDpDgxJgB7VyYjUD',
  [`/rural/${SURVEY_ID.SLOW_NO_GROWTH}`]: 'slownoGrowthSurvey',
  [`/demographics`]: 'thankYouPage',
  [`/thank-you`]: 'thankYouPage',
}

const ContentfulProvider = ({ children, locale }) => {
  const pathname = useSelector(({ global }) => global.pathname)
  const isLoading = useRef(false)
  const [content, setContent] = useState({})
  const [loadedID, setLoadedID] = useState('')
  const [loadedLocale, setLoadedLocale] = useState('')
  const [globalContent, setGlobalContent] = useState({})
  const [timeoutDialogContent, setTimeoutDialogContent] = useState({})

  useEffect(() => {
    const fetchData = async () => {
      isLoading.current = true
      setContent({})
      if (CONTENTFUL_IDS[pathname]) {
        if (pathname.includes('/topic') || pathname.includes('/big-ideas')) {
          const response = await client.getEntry(CONTENTFUL_IDS[pathname], { locale, include: 2 })
          if (response?.fields) setContent(response.fields)
        } else {
          const response = await client.getEntries({
            content_type: CONTENTFUL_IDS[pathname],
            locale,
          })
          
          if (response.items.length > 0) {
            setContent(response.items[0].fields)
          }
        }
        // Get Globals
        const response = await client.getEntries({
          content_type: 'globals',
          locale,
        })
        
        if (response.items.length > 0) {
          setGlobalContent(response.items[0].fields)
        }

        // Get Timeout Dialog
        const responseDialog = await client.getEntries({
          content_type: 'timeoutDialogSettings',
          locale, 
        })
        
        if (responseDialog.items.length > 0) {
          setTimeoutDialogContent(responseDialog.items[0].fields)
        }
      }
      isLoading.current = false
      setLoadedID(CONTENTFUL_IDS[pathname])
      setLoadedLocale(locale)
    }
    if (!isLoading.current && (loadedID !== CONTENTFUL_IDS[pathname] || loadedLocale !== locale)) {
      fetchData()
    }
  }, [locale, pathname, loadedID, loadedLocale])

  // if (!pathname) return null

  return (
    <ContentfulContext.Provider 
      value={{ ...content, ...globalContent, ...timeoutDialogContent }}
      // isLoading={isLoading}
    >
      {isEmpty(content) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'white',
            zIndex: 9999,
          }}
        />
      )}
      {children}
      {/* {!isLoading && children} */}
    </ContentfulContext.Provider>
  )
}

export default ContentfulProvider
